import {Component, effect, inject, input, signal} from "@angular/core";
import {IconConfig} from "@models";
import {FormBuilder, FormControl, FormGroup, FormRecord, ReactiveFormsModule} from "@angular/forms";
import {outputFromObservable} from "@angular/core/rxjs-interop";
import {filter, map} from "rxjs/operators";
import {IconDefinitions} from "../../rating-item-icon-sets-page.component";
import {TranslateModule} from "@ngx-translate/core";
import {NgxColorsModule, validColorValidator} from "ngx-colors";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {KeyValuePipe} from "@angular/common";
import {Translations} from "@translations";
import {MaterialModule} from "../../../../../../../../shared/modules/material.module";

type IconFormGroup = FormGroup<{
	icon: FormGroup<{
		fontSet: FormControl<string>;
		fontIcon: FormControl<string>;
	}>;
	inactiveColor: FormControl<string>;
	inactiveColorPicker: FormControl<string>;
	activeColor: FormControl<string>;
	activeColorPicker: FormControl<string>;
}>;

type RawFormValue = ReturnType<FormRecord<IconFormGroup>["getRawValue"]>;

function computeIconDefinitionsFromFormValue(rawValue: RawFormValue): IconDefinitions {
	return Object.entries(rawValue).reduce((iconDefinitions, [key, iconConfig]) => {
		iconDefinitions[parseInt(key)] = {
			icon: {
				fontIcon: iconConfig.icon.fontIcon,
				fontSet: iconConfig.icon.fontSet,
			},
			inactiveColor: iconConfig.inactiveColor,
			activeColor: iconConfig.activeColor,
		};
		return iconDefinitions;
	}, {} as IconDefinitions);
}

const defaultColor = "#D2D8DB";

@UntilDestroy()
@Component({
	selector: "sim-rating-item-icon-set-form",
	imports: [TranslateModule, NgxColorsModule, KeyValuePipe, ReactiveFormsModule, MaterialModule],
	templateUrl: "./rating-item-icon-set-form.component.html",
	styleUrl: "./rating-item-icon-set-form.component.scss",
})
export class RatingItemIconSetFormComponent {
	protected readonly T = Translations;

	// Injections
	readonly #fb = inject(FormBuilder).nonNullable;

	// Signals
	protected readonly iconColoring = signal<"active" | "inactive">("active");
	protected readonly selectedIcon = signal(0);

	// Properties
	protected readonly form = this.#fb.record<IconFormGroup>({});

	// IO
	public readonly iconDefinitions = input.required<IconDefinitions>();
	public readonly onIconDefinitionsChange = outputFromObservable(
		this.form.valueChanges.pipe(
			filter((_) => this.form.valid),
			// get the raw value because form fields cannot be disabled
			map((_) => computeIconDefinitionsFromFormValue(this.form.getRawValue())),
		),
	);

	// Effects
	readonly #initFormEffect = effect(() => {
		const iconDefinitions = this.iconDefinitions();

		// remove all previous controls
		for (const key in Object.keys(iconDefinitions)) {
			this.form.removeControl(key, {emitEvent: false});
		}

		this.addFormControls(iconDefinitions);

		this.syncColorFormControls();
	});

	protected addIcon(): void {
		const iconDefinitions = this.iconDefinitions();

		const key = Object.keys(iconDefinitions).length;
		const newFormGroup = this.#fb.group({
			icon: this.#fb.group({fontSet: "far", fontIcon: "fa-star"}),
			inactiveColor: this.#fb.control(defaultColor, {validators: [validColorValidator()]}),
			inactiveColorPicker: this.#fb.control(defaultColor),
			activeColor: this.#fb.control(defaultColor, {validators: [validColorValidator()]}),
			activeColorPicker: this.#fb.control(defaultColor),
		});

		this.form.addControl(String(key), newFormGroup);
	}

	protected removeIcon(): void {
		const iconDefinitions = this.iconDefinitions();
		const selectedIcon = this.selectedIcon();
		const lastIndex = Object.keys(iconDefinitions).length - 1;
		if (lastIndex === selectedIcon) {
			this.selectedIcon.set(Math.max(0, lastIndex - 1));
		}
		this.form.removeControl(String(lastIndex));
	}

	protected toggleIconColoring(): void {
		this.iconColoring.update((coloring) => (coloring === "active" ? "inactive" : "active"));
	}

	protected getColorForIconConfig(config: IconConfig): string | null {
		const color = this.iconColoring() === "active" ? config.activeColor : config.inactiveColor;
		if (!color) {
			return null;
		}
		return `${color} !important`;
	}

	private addFormControls(iconDefinitions: IconDefinitions): void {
		Object.entries(iconDefinitions).forEach(([key, value]) => {
			this.form.addControl(
				key,
				this.#fb.group({
					icon: this.#fb.group({
						fontSet: this.#fb.control(value.icon.fontSet),
						fontIcon: this.#fb.control(value.icon.fontIcon),
					}),
					inactiveColor: this.#fb.control(value.inactiveColor, {validators: [validColorValidator()]}),
					inactiveColorPicker: this.#fb.control(value.inactiveColor),
					activeColor: this.#fb.control(value.activeColor, {validators: [validColorValidator()]}),
					activeColorPicker: this.#fb.control(value.activeColor),
				}),
				{emitEvent: false},
			);
		});
	}

	private syncColorFormControls(): void {
		Object.keys(this.form.controls).forEach((key) => {
			const formGroup = this.form.controls[key];
			const formControlPairs = [
				[formGroup.controls.activeColor, formGroup.controls.activeColorPicker],
				[formGroup.controls.inactiveColor, formGroup.controls.inactiveColorPicker],
			] as const;

			formControlPairs.forEach(this.syncControlPair.bind(this));
		});
	}

	private syncControlPair([control1, control2]: readonly [FormControl<string>, FormControl<string>]): void {
		control1.valueChanges.pipe(untilDestroyed(this)).subscribe((color) => {
			control2.setValue(color, {emitEvent: false});
		});
		control2.valueChanges.pipe(untilDestroyed(this)).subscribe((color) => {
			control1.setValue(color, {emitEvent: false});
		});
	}
}
